import PlatformIcon from "@/components/dashboard/platform-icon"
import type { RealExample } from "@/data/real-examples"

/**
 * Distribution diagram — visual flow showing how QuiteRight turns a pile of
 * listing photos into a finished video and then publishes it to every
 * relevant social channel from a single click.
 *
 * Layout (desktop): three vertical "stages" sat side by side, joined by red
 * connector arrows that animate on reveal.
 *
 *   1. Photos     — a fanned stack of three real listing photos
 *   2. Video      — a single phone-shaped tile representing the rendered ad
 *   3. Channels   — a vertical column of the four social platforms with
 *                   "POSTED" tags on each
 *
 * Mobile collapses to a vertical stack with downward arrows between stages.
 *
 * The component takes a single `example` so the photos and video poster
 * stay in sync with the hero (which randomises across the example set).
 */

const CHANNELS: Array<{ platform: string; name: string; handleHint: string }> = [
  { platform: "instagram", name: "Instagram", handleHint: "Reels + feed" },
  { platform: "facebook", name: "Facebook", handleHint: "Page + groups" },
  { platform: "tiktok", name: "TikTok", handleHint: "Vertical post" },
  { platform: "youtube", name: "YouTube", handleHint: "Shorts + main" },
]

interface DistributionDiagramProps {
  example: RealExample
}

export function DistributionDiagram({ example }: DistributionDiagramProps) {
  return (
    <div className="relative">
      {/* Desktop / tablet — three columns side by side */}
      <div className="hidden items-stretch gap-0 md:grid md:grid-cols-[1fr_auto_1fr_auto_1.1fr]">
        <PhotosStage example={example} />
        <ConnectorRight label="Render" />
        <VideoStage example={example} />
        <ConnectorRight label="Publish" />
        <ChannelsStage />
      </div>

      {/* Mobile — vertical stack */}
      <div className="space-y-4 md:hidden">
        <PhotosStage example={example} />
        <ConnectorDown label="Render" />
        <VideoStage example={example} />
        <ConnectorDown label="Publish" />
        <ChannelsStage />
      </div>
    </div>
  )
}

/* ------------------------------------------------------------------ */
/* Stage 1 — fanned listing photos                                     */
/* ------------------------------------------------------------------ */

function PhotosStage({ example }: { example: RealExample }) {
  const photos = [example.propertyImage, ...example.thumbnails].slice(0, 4)
  return (
    <div className="flex flex-col items-center">
      <StageLabel index="I" title="Listing photos" sub={`${photos.length} images imported`} />
      <div className="relative mt-6 h-[220px] w-full max-w-[260px]">
        {photos.map((src, i) => {
          const rotations = ["-rotate-6", "-rotate-2", "rotate-3", "rotate-6"]
          const offsets = ["translate-y-0", "translate-y-1", "translate-y-2", "translate-y-3"]
          const z = photos.length - i
          return (
            <div
              key={src}
              className={`absolute left-1/2 top-0 h-[200px] w-[170px] -translate-x-1/2 ${rotations[i] ?? ""} ${offsets[i] ?? ""} overflow-hidden border-[6px] border-cream bg-cream shadow-[0_18px_40px_-18px_rgba(26,26,26,0.45)] transition-transform duration-500 hover:-translate-y-1`}
              style={{ zIndex: z }}
            >
              <img
                src={src}
                alt=""
                className="h-full w-full object-cover"
                loading="lazy"
                decoding="async"
              />
            </div>
          )
        })}
      </div>
      <p className="mt-5 max-w-[240px] text-center text-[12px] leading-relaxed text-slate">
        Pasted listing URL. Photos, price, address, and floorplan all imported automatically.
      </p>
    </div>
  )
}

/* ------------------------------------------------------------------ */
/* Stage 2 — rendered video tile                                       */
/* ------------------------------------------------------------------ */

function VideoStage({ example }: { example: RealExample }) {
  return (
    <div className="flex flex-col items-center">
      <StageLabel index="II" title="Cinematic video" sub="15 seconds, 9:16, branded" />
      <div className="relative mt-6">
        <div
          aria-hidden
          className="pointer-events-none absolute -inset-x-6 -inset-y-4"
          style={{
            background:
              "radial-gradient(ellipse 65% 55% at 50% 55%, rgba(196,30,42,0.32) 0%, rgba(196,30,42,0.06) 38%, transparent 72%)",
            filter: "blur(18px)",
          }}
        />
        <div className="relative h-[230px] w-[130px] overflow-hidden rounded-[26px] border-[3px] border-charcoal bg-charcoal shadow-[0_24px_50px_-22px_rgba(0,0,0,0.55)]">
          {/* Static still — always use propertyImage (the videoPoster URLs
              on some examples are unreliable). This tile is decorative;
              the real product video lives in the hero. */}
          <img
            src={example.propertyImage}
            alt=""
            className="h-full w-full object-cover"
            loading="lazy"
            decoding="async"
          />
          {/* Faux play overlay */}
          <div className="pointer-events-none absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-black/55" />
          <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
            <span className="flex size-12 items-center justify-center rounded-full bg-white/90 backdrop-blur shadow-lg">
              <svg viewBox="0 0 24 24" className="size-5 translate-x-[1px] fill-charcoal">
                <path d="M8 5v14l11-7z" />
              </svg>
            </span>
          </div>
          <div className="pointer-events-none absolute inset-x-2 bottom-2 text-[9px] font-semibold uppercase tracking-[0.18em] text-cream/90">
            {example.property}
          </div>
        </div>
        <span className="absolute -top-2 left-1/2 -translate-x-1/2 whitespace-nowrap bg-red px-2 py-[3px] text-[9px] font-semibold uppercase tracking-[0.18em] text-white shadow-md">
          Rendered
        </span>
      </div>
      <p className="mt-5 max-w-[200px] text-center text-[12px] leading-relaxed text-slate">
        Square, vertical, landscape. On-brand, voiced over, captioned, ready to ship.
      </p>
    </div>
  )
}

/* ------------------------------------------------------------------ */
/* Stage 3 — social channel column                                     */
/* ------------------------------------------------------------------ */

function ChannelsStage() {
  return (
    <div className="flex flex-col items-center">
      <StageLabel index="III" title="Posted everywhere" sub="One click, four platforms" />
      <ul className="mt-6 w-full max-w-[300px] space-y-3">
        {CHANNELS.map((channel) => (
          <li
            key={channel.platform}
            className="group flex items-center gap-4 border border-stone/80 bg-white px-4 py-3 shadow-[0_8px_24px_-18px_rgba(26,26,26,0.35)] transition-all duration-300 hover:-translate-y-px hover:border-charcoal/30"
          >
            <span className="flex size-10 shrink-0 items-center justify-center bg-charcoal/[0.04]">
              <PlatformIcon platform={channel.platform} className="size-5" />
            </span>
            <div className="flex-1 leading-tight">
              <p className="font-display text-[15px] font-bold text-charcoal">
                {channel.name}
              </p>
              <p className="text-[11px] text-warm-grey">{channel.handleHint}</p>
            </div>
            <span className="flex items-center gap-1 text-[9px] font-semibold uppercase tracking-[0.18em] text-red">
              <span className="size-1.5 rounded-full bg-red" />
              Posted
            </span>
          </li>
        ))}
      </ul>
      <p className="mt-5 max-w-[260px] text-center text-[12px] leading-relaxed text-slate">
        Schedule it, post it now, or download every aspect ratio. From one button.
      </p>
    </div>
  )
}

/* ------------------------------------------------------------------ */
/* Sub-bits                                                            */
/* ------------------------------------------------------------------ */

function StageLabel({ index, title, sub }: { index: string; title: string; sub: string }) {
  return (
    <div className="flex flex-col items-center text-center">
      <span className="font-display text-[12px] italic text-warm-grey">Stage {index}</span>
      <p className="mt-1 font-display text-[20px] font-bold tracking-tight text-charcoal sm:text-[22px]">
        {title}
        <span className="text-red">.</span>
      </p>
      <p className="mt-1 text-[10px] font-semibold uppercase tracking-[0.18em] text-warm-grey">
        {sub}
      </p>
    </div>
  )
}

/**
 * Horizontal connector with a thin red line and an arrow head. Used between
 * stages on desktop.
 */
function ConnectorRight({ label }: { label: string }) {
  return (
    <div className="relative flex w-16 flex-col items-center justify-center px-2 lg:w-20">
      <span className="mb-2 text-[9px] font-semibold uppercase tracking-[0.22em] text-red">
        {label}
      </span>
      <div className="relative h-[2px] w-full bg-red">
        <span
          aria-hidden
          className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-[1px] border-y-[5px] border-l-[8px] border-y-transparent border-l-red"
        />
      </div>
      <span className="mt-2 text-[9px] font-semibold uppercase tracking-[0.22em] text-warm-grey">
        Auto
      </span>
    </div>
  )
}

/**
 * Vertical connector for the mobile stack.
 */
function ConnectorDown({ label }: { label: string }) {
  return (
    <div className="relative flex flex-col items-center py-1">
      <span className="mb-1 text-[9px] font-semibold uppercase tracking-[0.22em] text-red">
        {label}
      </span>
      <div className="relative h-10 w-[2px] bg-red">
        <span
          aria-hidden
          className="absolute -bottom-[1px] left-1/2 -translate-x-1/2 border-x-[5px] border-t-[8px] border-x-transparent border-t-red"
        />
      </div>
    </div>
  )
}
